import { useEffect, useState } from 'react'
import styles from './PromptDrawer.module.scss'
import { XIcon, CheckIcon } from '@phosphor-icons/react'
import {
    setLastEditedPrompt,
    setPromptSelecting,
    setPromptSelectingObjects,
} from '../../../../store/actions/promptActions'
import { useDispatch, useSelector } from 'react-redux'
import { useApi } from '../../../../api/api'
import { updatePromptProps } from '../../../../store/actions'
import { NumericRange } from '../components/NumericRange/NumericRange'
import {
    ScoopSelector,
    ScoopButton,
    ScoopDrawer,
    ScoopSpinner,
    ScoopText,
    ScoopDatePicker,
    ScoopTextField,
    ScoopCheckbox,
    useToast,
} from '../../../ui'

export const PromptDrawer = ({ workspaceMetadata, currentPromptObject, workspaceID }) => {
    const dispatch = useDispatch()
    const { postData } = useApi()
    const toast = useToast()
    const userID = useSelector((state) => state.auth.userID)
    const promptSelecting = useSelector((state) => state.prompt.promptSelecting)
    const promptSelectingObjects = useSelector((state) => state.prompt.promptSelectingObjects)
    const [dataSources, setDataSources] = useState([])
    const [dataSource, setDataSource] = useState(null)
    const [field, setField] = useState(null)
    const [promptType, setPromptType] = useState('')
    const [label, setLabel] = useState('')
    const [categoryValues, setCategoryValues] = useState([])
    const [categoryValuesLoading, setCategoryValuesLoading] = useState(false)
    const [sheetLoading, setSheetLoading] = useState(false)
    const [singleSelectValue, setSingleSelectValue] = useState('')
    const [multipleSelectValue, setMultipleSelectValue] = useState([])
    const [dateValue, setDateValue] = useState(null)
    const [numericRange, setNumericRange] = useState(null)
    const [singleValue, setSingleValue] = useState(0)
    const [theme, setTheme] = useState('')
    const [dataSourceType, setDataSourceType] = useState('dataset')
    const [availableSheets, setAvailableSheets] = useState([])
    const [worksheet, setWorksheet] = useState(null)
    const [worksheetNamedRanges, setWorksheetNamedRanges] = useState([])
    const [selectedWorksheetRange, setSelectedWorksheetRange] = useState(null)
    const [worksheetColumns, setWorksheetColumns] = useState([])
    const [refreshInputQueries, setRefreshInputQueries] = useState(false)

    const getCategoryValues = (tableID, worksheetID, rangeName, columnName, likeValue) => {
        setCategoryValuesLoading(true)
        const action = {
            action: 'getCategoryValues',
            columnName: columnName,
            like: likeValue,
        }
        if (tableID) {
            action.reportSeriesTableID = tableID
        } else if (worksheetID) {
            action.worksheetID = worksheetID
            action.rangeName = rangeName
        }
        postData(action)
            .then((r) => {
                setCategoryValuesLoading(false)
                setCategoryValues(r.values)
            })
            .catch(() => {
                setCategoryValuesLoading(false)
                setCategoryValues([])
                toast.error('Error fetching values, try again later.')
            })
    }

    const getColumns = (worksheetID, namedRange) => {
        setCategoryValuesLoading(true)
        const action = {
            action: 'getColumns',
            worksheetID: worksheetID,
            namedRange: namedRange,
        }
        postData(action)
            .then((r) => {
                setWorksheetColumns(r.columns)
                if (currentPromptObject.promptProps.worksheetId) {
                    r?.columns.forEach((col) => {
                        if (col.columnName === currentPromptObject.promptProps.fieldName) {
                            setField(col)
                        }
                    })
                }
                setCategoryValuesLoading(false)
            })
            .catch(() => {
                setWorksheetColumns([])
                toast.error('Error fetching values, try again later.')
            })
    }

    useEffect(() => {
        if (workspaceID && userID) {
            postData({
                action: 'getObjects',
                class: 'scoop.worksheet.Worksheet',
                attributeName: 'workspaceID',
                attributeValue: workspaceID,
            }).then(({ objects: result }) => {
                let availableWorksheets = result.filter(
                    (wi) => wi.sheetType === 'DynamicSheet' || wi.sheetType === 'CalculatedSource'
                )
                availableWorksheets = availableWorksheets.sort((a, b) => {
                    if (a.label === null) return 1
                    if (b.label === null) return -1
                    return a.label.toLowerCase().localeCompare(b.label.toLowerCase())
                })
                setAvailableSheets(availableWorksheets)
            })
        }
    }, [workspaceID, currentPromptObject, userID])

    useEffect(() => {
        if (currentPromptObject?.promptProps?.worksheetId && availableSheets.length > 0) {
            availableSheets.forEach((sheet) => {
                if (sheet.worksheetID === currentPromptObject.promptProps.worksheetId) {
                    setWorksheet(sheet)
                }
            })
        }
    }, [currentPromptObject, availableSheets])

    useEffect(() => {
        if (currentPromptObject && workspaceMetadata && availableSheets) {
            const props = currentPromptObject.promptProps
            setLabel(props.label)
            setPromptType(props.type)
            setTheme(props.theme)
            if (props.worksheetId) {
                setDataSourceType('namedRange')
                setSheetRanges(props.worksheetId)
            } else {
                workspaceMetadata?.inboxes?.forEach((inbox) => {
                    inbox.tables.forEach((table) => {
                        if (table.reportSeriesTableID === props.dataSourceId) {
                            setDataSource(table)
                            table.columns.forEach((col) => {
                                if (col.columnName === props.fieldName) {
                                    setField(col)
                                }
                            })
                        }
                    })
                })
            }
            switch (props.type) {
                case 'single-select':
                    setSingleSelectValue(props.value || '')
                    break
                case 'multi-select':
                    setMultipleSelectValue(props.value || [])
                    break
                case 'single-date':
                    setDateValue(props.value ? new Date(props.value) : null)
                    break
                case 'date-range':
                    setDateValue(
                        props.value ? [new Date(props.value[0]), new Date(props.value[1])] : null
                    )
                    break
                case 'numeric-range':
                    setNumericRange(props.value || null)
                    break
                case 'single-value':
                    setSingleValue(props.value || 0)
                    break
            }
            setRefreshInputQueries(props.refreshInputQueries)
        }
    }, [currentPromptObject, availableSheets])

    useEffect(() => {
        if (workspaceMetadata) {
            let tables = []
            workspaceMetadata?.inboxes?.forEach((inbox) => {
                inbox.tables.forEach((table) => {
                    tables.push(table)
                })
            })
            tables = tables.map((table) => ({
                ...table,
                tableName: table.tableName || 'Unnamed',
            }))
            tables.sort((a, b) => a.tableName.localeCompare(b.tableName))
            setDataSources(tables)
        }
    }, [workspaceMetadata])

    useEffect(() => {
        if (field) {
            if (dataSource) {
                getCategoryValues(dataSource.reportSeriesTableID, null, null, field.columnName)
            } else if (worksheet) {
                getCategoryValues(
                    null,
                    worksheet.worksheetID,
                    selectedWorksheetRange,
                    field.columnName
                )
            }
        }
    }, [field])

    const handleClose = () => {
        setField(null)
        setDataSource(null)
        setLabel('')
        setPromptType('')
        setCategoryValues([])
        setDataSourceType('dataset')
        setWorksheet(null)
        setSingleSelectValue('')
        setMultipleSelectValue([])
        setDateValue(null)
        setNumericRange(null)
        setSingleValue(0)
        setTheme('')
        setWorksheetNamedRanges([])
        setSelectedWorksheetRange(null)
        setSheetLoading(false)
        setCategoryValuesLoading(false)
        setWorksheetColumns([])
        dispatch(setPromptSelecting(false, null))
        dispatch(setPromptSelectingObjects([]))
    }

    const handleSave = () => {
        let value
        let prompt = {
            attributeName: field?.columnName,
            operator: 'Equals',
            filterValue: { values: null },
        }
        switch (promptType) {
            case 'single-select':
                value = singleSelectValue
                prompt.filterValue.values = [singleSelectValue]
                break
            case 'multi-select':
                value = multipleSelectValue
                prompt.filterValue.values =
                    multipleSelectValue.length === categoryValues.length
                        ? ['All']
                        : multipleSelectValue
                break
            case 'single-date':
                value = dateValue.toString()
                prompt.filterValue.values = [dateValue.toISOString().split('T')[0]]
                break
            case 'date-range':
                prompt = [
                    {
                        attributeName: field?.columnName,
                        operator: 'GreaterThanOrEquals',
                        filterValue: {
                            values: [dateValue[0].toISOString().split('T')[0]],
                        },
                    },
                    {
                        attributeName: field?.columnName,
                        operator: 'LessThanOrEquals',
                        filterValue: {
                            values: [dateValue[1].toISOString().split('T')[0]],
                        },
                    },
                ]
                value = [dateValue[0].toString(), dateValue[1].toString()]
                break
            case 'numeric-range':
                prompt = [
                    {
                        attributeName: field?.columnName,
                        operator: 'GreaterThanOrEquals',
                        filterValue: { values: [numericRange[0]] },
                    },
                    {
                        attributeName: field?.columnName,
                        operator: 'LessThanOrEquals',
                        filterValue: { values: [numericRange[1]] },
                    },
                ]
                value = numericRange
                break
            case 'single-value':
                value = singleValue
                prompt.filterValue.values = [singleValue]
                break
        }
        const promptProps = {
            label: label,
            theme: theme || null,
            dataSourceId: dataSource?.reportSeriesTableID,
            worksheetId: worksheet?.worksheetID,
            rangeName: selectedWorksheetRange,
            fieldName: field?.columnName,
            type: promptType,
            value: value,
            prompt: prompt,
            objects: promptSelectingObjects,
        }
        if (refreshInputQueries) promptProps.refreshInputQueries = true
        dispatch(updatePromptProps(currentPromptObject.id, promptProps))
        dispatch(setLastEditedPrompt(promptProps))
        handleClose()
    }

    const handleChangeTheme = (themeID) => {
        if (themeID === 'None') setTheme(undefined)
        else setTheme(themeID)
    }

    const setSheetRanges = (worksheetID) => {
        setSheetLoading(true)
        postData({
            action: 'getSheetMetadata',
            worksheetID: worksheetID,
        })
            .then((result) => {
                if (result.namedRanges) {
                    setWorksheetNamedRanges(result.namedRanges)
                    if (currentPromptObject.promptProps.rangeName) {
                        getColumns(
                            currentPromptObject.promptProps.worksheetId,
                            currentPromptObject.promptProps.rangeName
                        )
                        setSelectedWorksheetRange(currentPromptObject.promptProps.rangeName)
                    }
                    setSheetLoading(false)
                }
            })
            .catch(() => {
                setSheetLoading(false)
            })
    }

    const handleSelectWorksheet = (worksheetID) => {
        for (let i = 0; i < availableSheets.length; i++) {
            if (availableSheets[i].worksheetID === worksheetID) {
                setWorksheet(availableSheets[i])
                setSheetRanges(worksheetID)
                break
            }
        }
    }

    // Build theme options
    const themeOptions = [
        { label: 'None', value: undefined },
        ...(workspaceMetadata?.themes?.map((t) => ({
            label: t.themeName ? `${t.themeName} (${t.canvasName})` : `${t.canvasName} (Default)`,
            value: t.themeID,
        })) || []),
    ]

    // Build source type options
    const sourceTypeOptions = [
        { label: 'Dataset', value: 'dataset' },
        { label: 'Live Sheet Range', value: 'namedRange' },
    ]

    // Build datasource options
    const datasourceOptions = dataSources.map((table) => ({
        label: table.tableName,
        value: table.reportSeriesTableID,
    }))

    // Build worksheet options
    const worksheetOptions = availableSheets.map((ws) => ({
        label: ws.label,
        value: ws.worksheetID,
    }))

    // Build named range options
    const namedRangeOptions = worksheetNamedRanges.map((nr) => ({
        label: nr,
        value: nr,
    }))

    // Build column options for dataset
    const dataSourceColumnOptions =
        dataSource?.columns
            ?.sort((a, b) => a.columnName.localeCompare(b.columnName))
            .map((col) => ({
                label: col.columnName,
                value: col.columnName,
            })) || []

    // Build column options for worksheet
    const worksheetColumnOptions = worksheetColumns
        ?.sort((a, b) => a.columnName.localeCompare(b.columnName))
        .map((col) => ({
            label: col.columnName,
            value: col.columnName,
        }))

    // Build prompt type options based on field type
    const getPromptTypeOptions = () => {
        if (!field) return []

        const options = []
        if (field.columnType === 'String') {
            options.push({ label: 'Single select', value: 'single-select' })
            options.push({ label: 'Multi select', value: 'multi-select' })
        } else if (field.columnType === 'DateTime') {
            options.push({ label: 'Date range', value: 'date-range' })
            options.push({ label: 'Single date', value: 'single-date' })
        } else if (
            field.columnType === 'Integer' ||
            field.columnType === 'Currency' ||
            field.columnType === 'Decimal'
        ) {
            options.push({ label: 'Numeric range', value: 'numeric-range' })
            options.push({ label: 'Single value', value: 'single-value' })
        }
        return options
    }

    // Build category value options
    const categoryValueOptions = categoryValues.map((value) => ({
        label: value,
        value: value,
        rightSection:
            singleSelectValue === value ? <CheckIcon size={16} className={styles.icon} /> : null,
    }))

    return (
        <ScoopDrawer
            variant="persistent"
            anchor="right"
            open={promptSelecting}
            onClose={handleClose}
            width="450px"
        >
            <div className={styles.drawerContent}>
                <div className={styles.drawerHeader}>
                    <ScoopText className={styles.headerTitle}>Prompt</ScoopText>
                    <ScoopButton variant="ghost" onClick={handleClose}>
                        <XIcon size={20} className={styles.icon} />
                    </ScoopButton>
                </div>
                <div className={styles.promptsControls}>
                    <ScoopTextField
                        label="Label"
                        className={styles.promptInput}
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                    />

                    {workspaceMetadata?.themes && (
                        <ScoopSelector
                            value={theme}
                            onChange={(e) => handleChangeTheme(e.target.value)}
                            options={themeOptions}
                            label="Color Theme"
                            labelClassName="selector-label-bold"
                            size="sm"
                        />
                    )}

                    <ScoopSelector
                        value={dataSourceType}
                        onChange={(e) => {
                            setDataSourceType(e.target.value)
                            if (e.target.value === 'dataset') {
                                setWorksheet(null)
                                setSelectedWorksheetRange(null)
                            } else {
                                setDataSource(null)
                            }
                        }}
                        options={sourceTypeOptions}
                        label="Source type"
                        labelClassName="selector-label-bold"
                        size="sm"
                    />

                    {dataSourceType === 'dataset' && (
                        <ScoopSelector
                            value={dataSource?.reportSeriesTableID}
                            onChange={(e) => {
                                const table = dataSources.find(
                                    (t) => t.reportSeriesTableID === e.target.value
                                )
                                setDataSource(table)
                                setField(null)
                                setPromptType('')
                            }}
                            options={datasourceOptions}
                            label="Source"
                            labelClassName="selector-label-bold"
                            size="sm"
                            searchable
                        />
                    )}

                    {dataSourceType === 'namedRange' && availableSheets && (
                        <>
                            <ScoopSelector
                                value={worksheet?.worksheetID}
                                onChange={(e) => handleSelectWorksheet(e.target.value)}
                                options={worksheetOptions}
                                label="Live Sheet Name"
                                labelClassName="selector-label-bold"
                                size="sm"
                                searchable
                            />

                            {sheetLoading ? (
                                <div className={styles.loadingBox}>
                                    <ScoopSpinner />
                                </div>
                            ) : (
                                worksheet && (
                                    <ScoopSelector
                                        value={selectedWorksheetRange}
                                        onChange={(e) => {
                                            setSelectedWorksheetRange(e.target.value)
                                            setField(null)
                                            getColumns(worksheet.worksheetID, e.target.value)
                                        }}
                                        options={namedRangeOptions}
                                        label="Named Range"
                                        labelClassName="selector-label-bold"
                                        size="sm"
                                    />
                                )
                            )}
                        </>
                    )}

                    {dataSource && (
                        <ScoopSelector
                            value={field?.columnName}
                            onChange={(e) => {
                                const col = dataSource.columns.find(
                                    (c) => c.columnName === e.target.value
                                )
                                setField(col)
                                setPromptType('')
                            }}
                            options={dataSourceColumnOptions}
                            label="Source column"
                            labelClassName="selector-label-bold"
                            size="sm"
                            searchable
                        />
                    )}

                    {worksheet &&
                        worksheetColumns &&
                        selectedWorksheetRange &&
                        !sheetLoading &&
                        (categoryValuesLoading ? (
                            <div className={styles.loadingBox}>
                                <ScoopSpinner />
                            </div>
                        ) : (
                            <ScoopSelector
                                value={field?.columnName}
                                onChange={(e) => {
                                    const col = worksheetColumns.find(
                                        (c) => c.columnName === e.target.value
                                    )
                                    setField(col)
                                    setPromptType('')
                                }}
                                options={worksheetColumnOptions}
                                label="Source column"
                                labelClassName="selector-label-bold"
                                size="sm"
                                searchable
                            />
                        ))}

                    {field &&
                        (!categoryValuesLoading ? (
                            <ScoopSelector
                                value={promptType}
                                onChange={(e) => {
                                    setPromptType(e.target.value)
                                    setSingleSelectValue('')
                                    setMultipleSelectValue([])
                                    setDateValue(null)
                                    setNumericRange(null)
                                }}
                                options={getPromptTypeOptions()}
                                label="Prompt type"
                                labelClassName="selector-label-bold"
                                size="sm"
                            />
                        ) : (
                            <div className={styles.loadingBox}>
                                <ScoopSpinner />
                            </div>
                        ))}

                    {!categoryValuesLoading && !sheetLoading && promptType === 'single-select' && (
                        <ScoopSelector
                            value={singleSelectValue}
                            onChange={(e) => setSingleSelectValue(e.target.value)}
                            options={categoryValueOptions}
                            label="Default value"
                            labelClassName="selector-label-bold"
                            size="sm"
                            searchable
                        />
                    )}

                    {!categoryValuesLoading && !sheetLoading && promptType === 'multi-select' && (
                        <ScoopSelector
                            value={multipleSelectValue}
                            onChange={(e) => setMultipleSelectValue(e.target.value)}
                            options={categoryValueOptions}
                            label="Default value"
                            placeholder="Select values..."
                            size="sm"
                            searchable
                            multiple
                            showChips
                            showSelectAll
                        />
                    )}

                    {!categoryValuesLoading &&
                        !sheetLoading &&
                        (promptType === 'date-range' || promptType === 'single-date') && (
                            <ScoopDatePicker
                                value={dateValue}
                                onChange={setDateValue}
                                range={promptType === 'date-range'}
                                style={{ marginTop: '20px' }}
                                label={'Default value'}
                            />
                        )}

                    {!categoryValuesLoading && !sheetLoading && promptType === 'numeric-range' && (
                        <NumericRange
                            label={'Default value'}
                            value={numericRange}
                            onChange={setNumericRange}
                            containerSx={{ marginTop: '20px' }}
                            max={parseInt(categoryValues[categoryValues.length - 1])}
                            min={categoryValues[0] !== null ? parseInt(categoryValues[0]) : 0}
                            popWidth={350}
                        />
                    )}

                    {!categoryValuesLoading && !sheetLoading && promptType === 'single-value' && (
                        <ScoopTextField
                            label="Single value"
                            className={styles.promptInput}
                            value={singleValue}
                            onChange={(e) => setSingleValue(e.target.value.replace(/\D/g, ''))}
                            type="number"
                        />
                    )}

                    {!categoryValuesLoading && !sheetLoading && worksheet && (
                        <ScoopCheckbox
                            label="Refresh worksheet queries"
                            checked={refreshInputQueries}
                            onChange={() => setRefreshInputQueries(!refreshInputQueries)}
                        />
                    )}
                </div>
                <div className={styles.promptButtonsContainer}>
                    <ScoopButton variant="text" onClick={handleClose}>
                        Cancel
                    </ScoopButton>
                    <ScoopButton variant="default" onClick={handleSave}>
                        Save Changes
                    </ScoopButton>
                </div>
            </div>
        </ScoopDrawer>
    )
}
